import SectionTitle from "./SectionTitle";
import supportedMethodsImg from "../../../assets/payment/supported-payment-methods.png";

type MethodKey =
  | "payid_osko"
  | "visa_mastercard"
  | "apple_google_pay"
  | "amopay_speedpay"
  | "crypto_btc"
  | "crypto_eth"
  | "crypto_usdt";

type PaymentMethod = {
  key: MethodKey;
  title: string;
  bestFor: string;
  speedDeposit: string;
  speedWithdraw: string;
  typicalFees: string;
  privacy: "Low" | "Medium" | "High";
  chargebackRisk: "Low" | "Medium" | "High";
  notes: string[];
};

const methods: PaymentMethod[] = [
  {
    key: "payid_osko",
    title: "PayID & Osko (Bank Transfer, AUD)",
    bestFor: "The easiest “set and forget” option for most Australians: fast deposits, straightforward withdrawals, and clean AUD tracking.",
    speedDeposit: "Usually instant (seconds to a few minutes)",
    speedWithdraw: "Often same day (varies by review + bank cutoffs)",
    typicalFees: "Usually none (your bank rules apply)",
    privacy: "Low",
    chargebackRisk: "Low",
    notes: [
      "If you’re not sure what to use, start here. It’s usually the smoothest path from deposit to withdrawal.",
      "PayID is the identifier (mobile, email, or ABN). Osko is the real‑time transfer rail used by many AU banks.",
      "Stays in AUD end‑to‑end, so there’s no hidden FX conversion surprise.",
      "Keep your profile name consistent with your bank details; mismatches are a common reason withdrawals get delayed.",
    ],
  },
  {
    key: "visa_mastercard",
    title: "Visa & Mastercard (Card)",
    bestFor: "Quick, familiar deposits when you just want to top up instantly and don’t want to jump through bank transfer steps.",
    speedDeposit: "Instant",
    speedWithdraw: "Varies (often slower than deposits; sometimes routed to bank transfer)",
    typicalFees: "Can be higher (card processing costs vary by provider)",
    privacy: "Low",
    chargebackRisk: "High",
    notes: [
      "Cards are great for deposits. For withdrawals, some platforms prefer bank rails instead.",
      "Because cards allow disputes/chargebacks, they usually trigger stricter checks and occasional friction.",
      "If you get a decline, try: complete 3DS, enable online payments, check daily limits, or test a smaller amount first.",
    ],
  },
  {
    key: "apple_google_pay",
    title: "Apple Pay & Google Pay (Wallet)",
    bestFor: "The fastest mobile UX: one tap, biometrics, and you’re done—ideal when you’re playing on a phone.",
    speedDeposit: "Instant",
    speedWithdraw: "Usually handled via another method (often bank transfer)",
    typicalFees: "Usually similar to card processing (provider-dependent)",
    privacy: "Medium",
    chargebackRisk: "Medium",
    notes: [
      "Wallets use tokenisation, so your card number isn’t exposed in the same way.",
      "Still follows your underlying bank/card rules (limits, risk checks, etc.).",
      "If it fails, re‑verify the wallet, check biometrics/passcode, and confirm the card is active inside the wallet.",
    ],
  },
  {
    key: "amopay_speedpay",
    title: "Amopay & Speedpay (Local/E-wallet options)",
    bestFor: "A practical fallback when your bank/card is picky—often still fast, and usually stays in AUD.",
    speedDeposit: "Fast (often instant)",
    speedWithdraw: "Varies (depends on provider + what withdrawals are enabled)",
    typicalFees: "Varies by provider",
    privacy: "Medium",
    chargebackRisk: "Medium",
    notes: [
      "Flows differ by provider (redirects, reference codes, confirmations), so follow the exact on‑screen steps.",
      "Save the receipt/reference. If a deposit needs tracing, that reference solves it quickly.",
    ],
  },
  {
    key: "crypto_btc",
    title: "Bitcoin (BTC)",
    bestFor: "When you care about portability and don’t mind waiting for confirmations (and sometimes higher network fees).",
    speedDeposit: "Depends on confirmations (can be minutes to longer)",
    speedWithdraw: "Processing + confirmations (timing varies)",
    typicalFees: "Network fees fluctuate (can spike during congestion)",
    privacy: "High",
    chargebackRisk: "Low",
    notes: [
      "Avoid tiny “urgent” transfers when the mempool is busy; fees can be painful.",
      "Triple‑check the address. Crypto transfers are typically irreversible once broadcast.",
    ],
  },
  {
    key: "crypto_eth",
    title: "Ethereum (ETH)",
    bestFor: "If you’re already using ETH—liquid and widely supported, but gas fees can be the deal breaker at peak times.",
    speedDeposit: "Depends on confirmations (often quick, sometimes not)",
    speedWithdraw: "Processing + confirmations (varies)",
    typicalFees: "Gas fees can be high during peak usage",
    privacy: "High",
    chargebackRisk: "Low",
    notes: [
      "If fees look crazy, waiting a bit can save money—gas changes throughout the day.",
      "Confirm the network you’re using. Mixing networks is a classic (and costly) mistake.",
    ],
  },
  {
    key: "crypto_usdt",
    title: "USDT (Stablecoin)",
    bestFor: "When you want crypto convenience without price swings—USDT keeps value steadier than BTC/ETH.",
    speedDeposit: "Fast (depends on network)",
    speedWithdraw: "Fast (depends on network)",
    typicalFees: "Usually low‑to‑medium (depends on network)",
    privacy: "High",
    chargebackRisk: "Low",
    notes: [
      "USDT exists on multiple networks. Always match the network shown in the cashier (this matters more than people think).",
      "Stable price doesn’t mean “no risk”—wrong network/address handling still causes losses.",
    ],
  },
];

function MiniToc() {
  const items = [
    { href: "#payment-guide-overview", label: "What each method is good for" },
    { href: "#payment-guide-comparison", label: "Side‑by‑side comparison" },
    { href: "#payment-guide-how-to-choose", label: "Pick the right one (common scenarios)" },
    { href: "#payment-guide-methods", label: "Method breakdowns (details)" },
    { href: "#payment-guide-security", label: "Safety checklist" },
    { href: "#payment-guide-troubleshooting", label: "If something fails" },
  ];

  return (
    <div className="glass-card rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <span
          className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(16,92,232,0.12)", border: "1px solid rgba(16,92,232,0.25)" }}
        >
          <i className="ri-file-list-3-line text-blue-400"></i>
        </span>
        <div>
          <div className="text-slate-100 font-semibold text-sm">Payment guide</div>
          <div className="text-slate-400 text-xs">Shortcuts to the parts you need</div>
        </div>
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((item) => (
          <li key={item.href}>
            <a
              href={item.href}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-300 transition-all duration-200 hover:text-blue-400"
              style={{ background: "rgba(255,255,255,0.02)" }}
            >
              <span className="text-slate-500">›</span>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pill({ children, tone }: { children: string; tone: "green" | "amber" | "red" | "blue" }) {
  const styles =
    tone === "green"
      ? { background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.25)", color: "#4ade80" }
      : tone === "amber"
        ? { background: "rgba(224,201,127,0.12)", border: "1px solid rgba(224,201,127,0.25)", color: "#e0c97f" }
        : tone === "red"
          ? { background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }
          : { background: "rgba(16,92,232,0.12)", border: "1px solid rgba(16,92,232,0.25)", color: "#60a5fa" };

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold" style={styles}>
      {children}
    </span>
  );
}

function RiskPill({ value }: { value: "Low" | "Medium" | "High" }) {
  if (value === "Low") return <Pill tone="green">Low</Pill>;
  if (value === "Medium") return <Pill tone="amber">Medium</Pill>;
  return <Pill tone="red">High</Pill>;
}

export default function PaymentMethodsGuide() {
  return (
    <section id="payment-guide" className="py-12">
      <SectionTitle>Payment Methods Guide & Comparison</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 mb-8">
        <div className="glass-card rounded-2xl p-6">
          <div id="payment-guide-overview" className="scroll-mt-24">
            <h3 className="text-white font-bold text-base mb-3">A practical way to pick a payment method</h3>
            <p className="text-slate-300 leading-relaxed text-sm">
              Most people only think about deposits. The smoother experience usually comes from thinking about the whole
              trip: deposit speed, withdrawal path, fees, and how much verification you might need. Below is a clear
              comparison plus a deeper breakdown for each method so you can choose without guessing.
            </p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.03]">
                <div className="text-slate-100 font-semibold text-sm mb-1">Fastest for AUD</div>
                <div className="text-slate-400 text-xs leading-relaxed">
                  PayID/Osko is often the quickest from deposit to withdrawal, especially if you want to stay in AUD.
                </div>
              </div>
              <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.03]">
                <div className="text-slate-100 font-semibold text-sm mb-1">Most convenient on mobile</div>
                <div className="text-slate-400 text-xs leading-relaxed">
                  Apple Pay / Google Pay is the easiest “tap and go” option on a phone.
                </div>
              </div>
              <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.03]">
                <div className="text-slate-100 font-semibold text-sm mb-1">Most private route</div>
                <div className="text-slate-400 text-xs leading-relaxed">
                  Crypto (BTC/ETH/USDT) can be more private, but you must match network + address exactly.
                </div>
              </div>
            </div>
          </div>
        </div>

        <MiniToc />
      </div>

      <div id="payment-guide-comparison" className="scroll-mt-24 glass-card rounded-2xl p-6 mb-8">
        <h3 className="text-white font-bold text-base mb-2">Comparison table (quick decision)</h3>
        <p className="text-slate-400 text-xs mb-4">
          Speeds and fees vary by bank/provider and can change. Use this as a practical guide to “what usually happens”.
        </p>

        <div className="comparison-table-scroll overflow-x-auto pb-0.5">
          <table className="min-w-[980px] w-full text-left border-separate border-spacing-0">
            <thead>
              <tr className="text-slate-200 text-xs">
                {["Method", "Best for", "Deposit speed", "Withdraw speed", "Typical fees", "Privacy", "Chargeback risk"].map(
                  (h) => (
                    <th
                      key={h}
                      className="py-3 px-3 bg-white/[0.04] border-b border-white/10 font-semibold"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {methods.map((m) => (
                <tr key={m.key} className="text-slate-300 text-xs">
                  <td className="py-3 px-3 border-b border-white/10">
                    <a href={`#payment-guide-${m.key}`} className="text-blue-300 hover:text-blue-400 font-semibold">
                      {m.title}
                    </a>
                  </td>
                  <td className="py-3 px-3 border-b border-white/10">{m.bestFor}</td>
                  <td className="py-3 px-3 border-b border-white/10">{m.speedDeposit}</td>
                  <td className="py-3 px-3 border-b border-white/10">{m.speedWithdraw}</td>
                  <td className="py-3 px-3 border-b border-white/10">{m.typicalFees}</td>
                  <td className="py-3 px-3 border-b border-white/10">
                    <RiskPill value={m.privacy} />
                  </td>
                  <td className="py-3 px-3 border-b border-white/10">
                    <RiskPill value={m.chargebackRisk} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div id="payment-guide-how-to-choose" className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-white font-bold text-base mb-3">How to choose (real scenarios)</h3>
          <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
            <div>
              <Pill tone="green">Recommended default</Pill>
              <div className="mt-2">
                If you’re in Australia and want the smoothest deposit + withdrawal path, start with{" "}
                <span className="text-slate-100 font-semibold">PayID/Osko</span>.
              </div>
            </div>
            <div>
              <Pill tone="blue">Mobile convenience</Pill>
              <div className="mt-2">
                If you mostly play on a phone,{" "}
                <span className="text-slate-100 font-semibold">Apple Pay / Google Pay</span> reduces friction—just keep
                in mind withdrawals often go via bank transfer.
              </div>
            </div>
            <div>
              <Pill tone="amber">Fallback methods</Pill>
              <div className="mt-2">
                If your bank/card is strict, consider{" "}
                <span className="text-slate-100 font-semibold">Amopay / Speedpay</span> as alternatives.
              </div>
            </div>
            <div>
              <Pill tone="red">Crypto caution</Pill>
              <div className="mt-2">
                Crypto is great for privacy and portability, but you must match{" "}
                <span className="text-slate-100 font-semibold">network + address</span> exactly—mistakes are often
                irreversible.
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-white font-bold text-base mb-3">Withdrawal-first mindset</h3>
          <p className="text-slate-300 text-xs leading-relaxed mb-3">
            A common mistake is choosing a deposit method that’s easy today but complicates withdrawal later. To avoid
            delays:
          </p>
          <ul className="space-y-2 text-xs text-slate-300">
            {[
              "Use the same identity and name across payment method and account profile.",
              "Keep proof of payment (receipt/reference) for manual tracing if needed.",
              "Avoid switching methods frequently right before a withdrawal request.",
              "If you plan to withdraw in AUD, prefer PayID/Bank Transfer rails from the start.",
            ].map((t) => (
              <li key={t} className="flex gap-2">
                <span className="text-slate-500">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-white font-bold text-base mb-3">Fees & speed: what really matters</h3>
          <ul className="space-y-2 text-xs text-slate-300 leading-relaxed">
            {[
              "Bank rails are typically best for predictable AUD transfers and simple bookkeeping.",
              "Cards/wallets are fastest for deposits, but can have higher processing cost and stricter review.",
              "Crypto speed depends on confirmations; fees depend on network congestion and token/network choice.",
              "Always consider the full journey: deposit cost + withdrawal cost + time + support overhead.",
            ].map((t) => (
              <li key={t} className="flex gap-2">
                <span className="text-slate-500">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div id="payment-guide-methods" className="scroll-mt-24">
        <h3 className="text-white font-bold text-base mb-4">Deep dives (each method)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {methods.map((m) => (
            <article
              key={m.key}
              id={`payment-guide-${m.key}`}
              className="glass-card rounded-2xl p-6 scroll-mt-24"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-slate-100 font-bold text-sm">{m.title}</h4>
                  <div className="text-slate-400 text-xs mt-1">{m.bestFor}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-[11px]">Privacy</span>
                    <RiskPill value={m.privacy} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-[11px]">Chargeback</span>
                    <RiskPill value={m.chargebackRisk} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl p-3 border border-white/10 bg-white/[0.03]">
                  <div className="text-slate-400 text-[11px] mb-1">Deposit speed</div>
                  <div className="text-slate-200 text-xs font-semibold">{m.speedDeposit}</div>
                </div>
                <div className="rounded-xl p-3 border border-white/10 bg-white/[0.03]">
                  <div className="text-slate-400 text-[11px] mb-1">Withdraw speed</div>
                  <div className="text-slate-200 text-xs font-semibold">{m.speedWithdraw}</div>
                </div>
                <div className="rounded-xl p-3 border border-white/10 bg-white/[0.03] col-span-2">
                  <div className="text-slate-400 text-[11px] mb-1">Typical fees</div>
                  <div className="text-slate-200 text-xs font-semibold">{m.typicalFees}</div>
                </div>
              </div>

              <ul className="space-y-2 text-xs text-slate-300 leading-relaxed">
                {m.notes.map((t, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="text-slate-500">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>

      <div id="payment-guide-security" className="scroll-mt-24 glass-card rounded-2xl p-6 mt-8">
        <h3 className="text-white font-bold text-base mb-3">Safety checklist (worth doing once)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 text-xs text-slate-300 leading-relaxed">
          <div className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]">
            <div className="text-slate-100 font-semibold text-sm mb-2">Name matching</div>
            <p className="text-slate-300">
              Use the same legal name across your account profile and payment method. Inconsistent names are a top cause
              of withdrawal delays.
            </p>
          </div>
          <div className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]">
            <div className="text-slate-100 font-semibold text-sm mb-2">Keep proof</div>
            <p className="text-slate-300">
              Save receipts, references, and transaction hashes (crypto). If a transfer needs manual tracing, these are
              your fastest path to resolution.
            </p>
          </div>
          <div className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]">
            <div className="text-slate-100 font-semibold text-sm mb-2">Crypto checklist</div>
            <ul className="space-y-2">
              {[
                "Confirm the network (e.g. USDT network) before sending.",
                "Send a small test transfer first for new addresses.",
                "Never reuse addresses copied from untrusted sources.",
                "Beware of malware that swaps clipboard addresses.",
              ].map((t) => (
                <li key={t} className="flex gap-2">
                  <span className="text-slate-500">•</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div id="payment-guide-troubleshooting" className="scroll-mt-24 glass-card rounded-2xl p-6 mt-8">
        <h3 className="text-white font-bold text-base mb-3">When something doesn’t work (quick fixes)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-xs text-slate-300 leading-relaxed">
          <div className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]">
            <div className="text-slate-100 font-semibold text-sm mb-2">Card declined / wallet fails</div>
            <ul className="space-y-2">
              {[
                "Check online/international payments settings in your banking app.",
                "Confirm 3D Secure (3DS) prompts are completed.",
                "Try a smaller amount to test limits, then increase gradually.",
                "If using Apple/Google Pay, ensure the underlying card is active and verified.",
              ].map((t) => (
                <li key={t} className="flex gap-2">
                  <span className="text-slate-500">•</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]">
            <div className="text-slate-100 font-semibold text-sm mb-2">Crypto pending too long</div>
            <ul className="space-y-2">
              {[
                "Verify you sent on the correct network and to the correct address.",
                "Check confirmation count on a reputable block explorer.",
                "If fees were set too low (where applicable), the transaction can take longer.",
                "Keep your transaction hash ready for support if the crediting is delayed.",
              ].map((t) => (
                <li key={t} className="flex gap-2">
                  <span className="text-slate-500">•</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

